import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme/variables';
import athletoLogoLight from '../assets/athleto_new.png';
import athletoLogoDark from '../assets/athleto_new_dark.png';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../contexts/UserContextNew';
import { SocialService } from '../services/socialService';
import { Notification } from '../types/user';
import { timeAgo } from '../i18n/format';
import LanguageSwitcher from './LanguageSwitcher';

interface TopHeaderProps {
  onMenuClick: () => void;
  isMobile?: boolean;
}

export default function TopHeader({ onMenuClick, isMobile = false }: TopHeaderProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useTranslation('nav');

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = SocialService.subscribeToUserNotifications(
        user.uid,
        (newNotifications) => {
          setNotifications(newNotifications.slice(0, 5));
          setUnreadCount(newNotifications.filter(n => !n.read).length);
        }
      );

      return () => unsubscribe();
    }
  }, [user?.uid]);

  const handleNotificationClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationItemClick = async (notification: Notification) => {
    if (!notification.read && user?.uid) {
      try {
        await SocialService.markNotificationAsRead(user.uid, notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {      }
    }

    handleClose();

    if (notification.type === 'message' || notification.type === 'channel_message') {
      if (notification.conversation_id) {
        navigate(`/messages?conversation=${notification.conversation_id}`);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconSx = { fontSize: 18 };
    switch (type) {
      case 'like':
        return <FavoriteIcon sx={{ ...iconSx, color: 'error.main' }} />;
      case 'comment':
        return <CommentIcon sx={{ ...iconSx, color: 'primary.main' }} />;
      case 'follow':
        return <PersonAddIcon sx={{ ...iconSx, color: 'success.main' }} />;
      case 'team_invite':
        return <GroupIcon sx={{ ...iconSx, color: 'info.main' }} />;
      case 'challenge':
        return <EmojiEventsIcon sx={{ ...iconSx, color: 'warning.main' }} />;
      case 'mention':
        return <AlternateEmailIcon sx={{ ...iconSx, color: 'secondary.main' }} />;
      case 'message':
        return <ChatIcon sx={{ ...iconSx, color: 'primary.main' }} />;
      case 'channel_message':
        return <ForumIcon sx={{ ...iconSx, color: 'info.main' }} />;
      default:
        return <NotificationsIcon sx={iconSx} />;
    }
  };

  const relativeTime = (date: Date) => {
    const { key, count } = timeAgo(date, { short: true });
    return t(`common:${key}`, { count });
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: colors.background.paper,
          borderBottom: `1px solid ${colors.border.default}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {/* Hamburger Menu */}
          <IconButton
            color="inherit"
            aria-label={t('common:actions.toggleSidebar')}
            edge="start"
            onClick={onMenuClick}
            sx={{
              color: 'text.primary',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo and Brand */}
          <Box
            component={Link}
            to="/teams"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              gap: 1,
            }}
          >
            <Box
              component="img"
              {...({ src: theme.palette.mode === 'dark' ? athletoLogoDark : athletoLogoLight, alt: "Athleto" } as any)}
              sx={{
                height: 32,
                width: 'auto',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                background: `linear-gradient(135deg, ${colors.white.full} 0%, ${colors.primary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Athleto
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications Bell */}
          {user && (
            <IconButton
              onClick={handleNotificationClick}
              sx={{ color: 'text.primary' }}
            >
              <Badge badgeContent={unreadCount} color="error" max={9}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Notifications Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxHeight: 480,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>
            {t('notifications:center.title')}
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('notifications:center.unreadCount', { count: unreadCount })}
            </Typography>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('notifications:center.empty')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  onClick={() => handleNotificationItemClick(notification)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'background.default',
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={notification.read ? 400 : 600}
                        noWrap
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(notification.created_at)}
                      </Typography>
                    }
                  />
                  {!notification.read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        ml: 1,
                      }}
                    />
                  )}
                </ListItem>
                {index < notifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
          >
            {t('notifications:center.viewAllButton')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
